import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  HostListener,
  Inject,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';

export interface TocSection {
  id: string;
  label: string;
  active: boolean;
}

@Component({
    selector: 'app-cancellation-policy',
    imports: [CommonModule, RouterModule],
    templateUrl: './cancellation-policy.component.html',
    styleUrl: './cancellation-policy.component.css'
})
export class CancellationPolicyComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  lastUpdated = '5th March 2026';

  tocSections: TocSection[] = [
    { id: 'c1', label: '1. Introduction', active: false },
    { id: 'c2', label: '2. The Absolute No-Refund Rule', active: false },
    { id: 'c3', label: '3. How to Cancel', active: false },
    { id: 'c4', label: '4. What Happens After Cancellation', active: false },
    { id: 'c5', label: '5. Monthly Subscriptions', active: false },
    { id: 'c6', label: '6. Annual Subscriptions', active: false },
    { id: 'c7', label: '7. Credits & Add-ons', active: false },
    { id: 'c8', label: '8. Auto-Renewal', active: false },
    { id: 'c9', label: '9. Plan Downgrades', active: false },
    { id: 'c10', label: '10. Cancellation by Us', active: false },
    { id: 'c11', label: '11. Beta Platform', active: false },
    { id: 'c12', label: '12. Data After Cancellation', active: false },
    { id: 'c13', label: '13. Governing Law', active: false },
    { id: 'c14', label: '14. Changes to Policy', active: false },
    { id: 'c15', label: '15. Contact', active: false },
  ];

  private intersectionObserver?: IntersectionObserver;
  private isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private meta: Meta,
    private titleService: Title,
    private renderer: Renderer2,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.setMetaTags();
    this.applySecurityHeaders();
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) this.initScrollSpy();
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
  }

  private setMetaTags(): void {
    const title =
      'Cancellation Policy — Angularize.dev by Pythrust Technologies';
    this.titleService.setTitle(title);
    const desc =
      'Angularize.dev Cancellation Policy — how to cancel your subscription, what happens to your access and data, and our strict no-refund stance on all plan types.';
    this.meta.updateTag({ name: 'description', content: desc });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: desc });
    this.meta.updateTag({
      property: 'og:url',
      content: 'https://angularize.dev/cancellation-policy',
    });
    if (this.isBrowser)
      this.ensureCanonical('https://angularize.dev/cancellation-policy');
  }

  private ensureCanonical(url: string): void {
    let link = document.querySelector(
      "link[rel='canonical']",
    ) as HTMLLinkElement;
    if (!link) {
      link = this.renderer.createElement('link') as HTMLLinkElement;
      this.renderer.setAttribute(link, 'rel', 'canonical');
      this.renderer.appendChild(document.head, link);
    }
    this.renderer.setAttribute(link, 'href', url);
  }

  private applySecurityHeaders(): void {
    if (!this.isBrowser) return;
    this.meta.updateTag({
      name: 'referrer',
      content: 'strict-origin-when-cross-origin',
    });
    this.meta.updateTag({
      'http-equiv': 'X-Content-Type-Options',
      content: 'nosniff',
    });
    this.meta.updateTag({ 'http-equiv': 'X-Frame-Options', content: 'DENY' });
    this.meta.updateTag({
      'http-equiv': 'Content-Security-Policy',
      content: [
        "default-src 'self'",
        "script-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
        "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://fonts.gstatic.com",
        "font-src 'self' https://fonts.gstatic.com",
        "img-src 'self' data: https:",
        "connect-src 'self'",
        "frame-ancestors 'none'",
        "base-uri 'self'",
        "form-action 'self'",
      ].join('; '),
    });
  }

  private initScrollSpy(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.tocSections.forEach(
              (s) => (s.active = s.id === entry.target.id),
            );
          }
        });
      },
      { rootMargin: '-15% 0px -80% 0px' },
    );
    this.tocSections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) this.intersectionObserver!.observe(el);
    });
  }

  scrollTo(id: string): void {
    if (!this.isBrowser) return;
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.isBrowser) window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
